// ─── Performance Budget — Core Web Vitals monitoring ────────────────────────
// Measures FCP, LCP, FID, CLS, TTFB via PerformanceObserver API and compares
// against hard budgets. Used by the founder-only Performance panel in Settings.

interface WebVitals {
  fcp: number | null;   // First Contentful Paint
  lcp: number | null;   // Largest Contentful Paint
  fid: number | null;   // First Input Delay
  cls: number | null;   // Cumulative Layout Shift
  ttfb: number | null;  // Time to First Byte
}

const BUDGETS = {
  fcp: 1800,   // ms
  lcp: 2500,   // ms
  fid: 100,    // ms
  cls: 0.1,    // score
  ttfb: 800,   // ms
  bundleKb: 500, // max JS bundle size
};

export function measureWebVitals(callback: (vitals: WebVitals) => void): void {
  const vitals: WebVitals = { fcp: null, lcp: null, fid: null, cls: null, ttfb: null };

  // FCP
  const fcpObserver = new PerformanceObserver((list) => {
    const entry = list.getEntries().find(e => e.name === 'first-contentful-paint');
    if (entry) { vitals.fcp = entry.startTime; callback({ ...vitals }); }
  });
  try { fcpObserver.observe({ type: 'paint', buffered: true }); } catch { /* unsupported */ }

  // LCP
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const last = entries[entries.length - 1];
    if (last) { vitals.lcp = last.startTime; callback({ ...vitals }); }
  });
  try { lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true }); } catch { /* unsupported */ }

  // FID
  const fidObserver = new PerformanceObserver((list) => {
    const entry = list.getEntries()[0] as PerformanceEventTiming | undefined;
    if (entry) { vitals.fid = entry.processingStart - entry.startTime; callback({ ...vitals }); }
  });
  try { fidObserver.observe({ type: 'first-input', buffered: true }); } catch { /* unsupported */ }

  // CLS
  let clsValue = 0;
  const clsObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (!(entry as any).hadRecentInput) { clsValue += (entry as any).value; }
    }
    vitals.cls = clsValue;
    callback({ ...vitals });
  });
  try { clsObserver.observe({ type: 'layout-shift', buffered: true }); } catch { /* unsupported */ }

  // TTFB
  const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (navEntry) {
    vitals.ttfb = navEntry.responseStart - navEntry.requestStart;
    callback({ ...vitals });
  }
}

export function checkBudgets(vitals: WebVitals): Array<{ metric: string; value: number; budget: number; over: boolean }> {
  const checks: Array<{ metric: string; value: number; budget: number; over: boolean }> = [];
  if (vitals.fcp != null) checks.push({ metric: 'FCP', value: vitals.fcp, budget: BUDGETS.fcp, over: vitals.fcp > BUDGETS.fcp });
  if (vitals.lcp != null) checks.push({ metric: 'LCP', value: vitals.lcp, budget: BUDGETS.lcp, over: vitals.lcp > BUDGETS.lcp });
  if (vitals.fid != null) checks.push({ metric: 'FID', value: vitals.fid, budget: BUDGETS.fid, over: vitals.fid > BUDGETS.fid });
  if (vitals.cls != null) checks.push({ metric: 'CLS', value: vitals.cls, budget: BUDGETS.cls, over: vitals.cls > BUDGETS.cls });
  if (vitals.ttfb != null) checks.push({ metric: 'TTFB', value: vitals.ttfb, budget: BUDGETS.ttfb, over: vitals.ttfb > BUDGETS.ttfb });
  return checks;
}

export { BUDGETS };
export type { WebVitals };
